/**
 * SharedTransactionsDrilldownModal - Modal wrapper for the shared TransactionTable
 *
 * This component wraps the shared TransactionTable from @techstack/ui with:
 * - TekStak's modal shell (overlay, header, chips, close button)
 * - TekStak-specific render slots (ClassificationChip, PoLink, flag/agent buttons)
 * - React-query integration via the fetchTransactions prop
 *
 * Migration path: Replaces the old TransactionsDrilldownModal one attach point at a time.
 */

import React, { useState, useEffect, useCallback, useMemo, createContext, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Database, AlertCircle, Loader2, Flag, Info, Briefcase } from 'lucide-react';
import { TransactionTable } from '@techstack/ui';
import '@techstack/ui/dist/styles/transaction-table.css';
import PoLink from '../../PoDetailModal/PoLink';
import ClassificationChip from '../../ClassificationChip';
import OverlayPortal from '../OverlayPortal';
import useModalBehavior from '../../../hooks/useModalBehavior';
import { ClassificationAgentTrigger } from '../../ClassificationAgent';
import './SharedTransactionsDrilldownModal.css';

// Context to pass refetch callback to ClassificationChip instances
const RefetchContext = createContext(() => {});

// Wrapper for ClassificationChip that picks up the refetch context
function ChipCellWrapper({ row }) {
  const onUpdated = useContext(RefetchContext);
  if (!row || !row.id) return row?.component_name || '—';
  return (
    <ClassificationChip
      itemId={row.id}
      componentName={row.component_name}
      confidence={row.component_confidence}
      classificationMethod={row.classification_method}
      onUpdated={onUpdated}
    />
  );
}

// Flag button component
function FlagButton({ row, flagged, onFlag, busy }) {
  if (!onFlag) return null;
  return (
    <button
      type="button"
      className={`stm-flag-btn ${flagged ? 'is-flagged' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onFlag(row, flagged);
      }}
      disabled={busy}
      title={flagged ? 'Remove flag' : 'Flag for review'}
    >
      <Flag size={14} />
    </button>
  );
}

// Actions cell (flag + agent trigger)
function ActionsCellWrapper({ row, flaggedSet, flagBusy, onFlag }) {
  const flagged = flaggedSet.has(row.id);
  const busy = flagBusy.has(row.id);

  return (
    <div className="stm-actions-cell">
      {onFlag && (
        <FlagButton row={row} flagged={flagged} onFlag={onFlag} busy={busy} />
      )}
      <ClassificationAgentTrigger
        itemId={row.id}
        description={row.item_description}
        supplier={row.supplier_name}
        amount={row.line_total}
        componentName={row.component_name}
        componentId={row.tech_component_id}
        confidence={row.component_confidence}
        method={row.classification_method}
        documentNumber={row.purchase_document_num}
        source="drilldown"
        variant="icon"
      />
    </div>
  );
}

/**
 * Main SharedTransactionsDrilldownModal component
 */
function SharedTransactionsDrilldownModal({
  isOpen,
  onClose,

  // Header
  title,
  contextLabel,
  chips = [],

  // Data (internal fetch via react-query)
  queryKey,
  queryFn,
  // Or pass pre-fetched data directly:
  rows: rowsProp,
  isLoading: isLoadingProp,
  error: errorProp,

  // Optional sections
  metaBlock = null,        // { description?, capability? } or null
  footerNote = null,

  // Custom columns (override default)
  columns = null,

  // Flag callback
  onFlagForReview = null,
  flaggedField = 'open_flag_count',

  // Copy context
  copyContext = null,

  // Vendor for display
  vendor,

  // Additional filters for TransactionTable
  departmentId,
  component,
  productFamily,
  startDate,
  endDate,

  // Confidence mode
  defaultConfidenceMode = 'confirmed',

  // Time period filter (shows 1W/1M/3M/6M/1Y/2Y/3Y/5Y/All pills)
  showTimePeriodFilter = true,
}) {
  const queryClient = useQueryClient();

  // Flag state
  const [flagBusy, setFlagBusy] = useState(new Set());
  const [flagOverrides, setFlagOverrides] = useState(new Map());

  /*
   * THE HOUSE MODAL BEHAVIOUR, which this modal did not have.
   *
   * It declared `aria-modal="true"` — which tells assistive tech that
   * everything outside it is inert — and then never moved focus inside.
   * Measured on the live board: with the drill open over 50 controls, focus
   * was still on the heatmap cell behind it, i.e. parked on an element the
   * screen reader had just been told to ignore. Three Tabs from there landed
   * on an SVG `<path>` in a chart BEHIND the overlay, and closing with the
   * Close button dropped focus to `<body>` — a keyboard reader was returned
   * to the top of the document rather than to the cell they came from.
   *
   * `useModalBehavior` has done all of this correctly since UX-AUDIT-2026-07
   * X-3 and seven other modals already use it. This one — the drill behind
   * every cell, bar and row on the account board, mounted from eleven call
   * sites — did not.
   *
   * It also fixes the backdrop. The old rule closed on any click whose target
   * resolved to the overlay, and a drag that STARTS on a table cell and ends
   * outside the panel resolves exactly that way: measured mousedown on
   * `tt-td`, mouseup on `.stm-overlay`, click on `.stm-overlay`. Selecting a
   * PO number and releasing past the edge threw the panel away. The hook
   * requires both ends on the backdrop.
   */
  const modal = useModalBehavior(onClose, { enabled: isOpen });

  // Body scroll lock — the hook does not own this one.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // Reset transient state when (re)opening
  useEffect(() => {
    if (isOpen) {
      setFlagOverrides(new Map());
      setFlagBusy(new Set());
    }
  }, [isOpen]);

  // Internal fetch (only when a queryFn is provided AND no external rows)
  const internalQuery = useQuery({
    queryKey: queryKey || ['stm-noop'],
    queryFn: queryFn || (() => Promise.resolve({ rows: [] })),
    enabled: isOpen && !!queryFn && rowsProp === undefined,
  });

  // Refetch callback for ClassificationChip
  const handleChipUpdated = useCallback(() => {
    if (queryKey && rowsProp === undefined) {
      queryClient.invalidateQueries({ queryKey });
    }
  }, [queryClient, queryKey, rowsProp]);

  // Derive rows from props or internal query
  const allRows = useMemo(() => {
    if (rowsProp !== undefined) return rowsProp;
    return internalQuery.data?.rows || internalQuery.data?.transactions || [];
  }, [rowsProp, internalQuery.data]);

  const isLoading = isLoadingProp !== undefined ? isLoadingProp : internalQuery.isLoading;
  const error = errorProp !== undefined ? errorProp : internalQuery.error;

  // Compute flagged set from rows + overrides
  const flaggedSet = useMemo(() => {
    const set = new Set();
    for (const r of allRows) {
      let fromRow = false;
      if (flaggedField) {
        const v = r[flaggedField];
        fromRow = typeof v === 'number' ? v > 0 : Boolean(v);
      }
      const isFlagged = flagOverrides.has(r.id) ? flagOverrides.get(r.id) : fromRow;
      if (isFlagged) set.add(r.id);
    }
    return set;
  }, [allRows, flagOverrides, flaggedField]);

  // Handle flag click
  const handleFlag = useCallback(async (row, currentlyFlagged) => {
    if (!onFlagForReview) return;
    setFlagBusy((prev) => new Set(prev).add(row.id));
    // Optimistic update
    setFlagOverrides((prev) => {
      const next = new Map(prev);
      next.set(row.id, !currentlyFlagged);
      return next;
    });
    try {
      await onFlagForReview(row, currentlyFlagged);
    } catch (err) {
      // Roll back optimistic flag
      setFlagOverrides((prev) => {
        const next = new Map(prev);
        next.set(row.id, currentlyFlagged);
        return next;
      });
      console.warn('Flag operation failed:', err);
    } finally {
      setFlagBusy((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  }, [onFlagForReview]);

  // Render slots for TransactionTable
  const renderClassification = useCallback((row) => (
    <ChipCellWrapper row={row} />
  ), []);

  const renderPoLink = useCallback((row) => (
    <PoLink
      documentNumber={row.purchase_document_num}
      departmentId={row.department_id}
      startDate={row.start_date}
    />
  ), []);

  const renderFooterActions = useCallback((row) => (
    <ActionsCellWrapper
      row={row}
      flaggedSet={flaggedSet}
      flagBusy={flagBusy}
      onFlag={onFlagForReview ? handleFlag : null}
    />
  ), [flaggedSet, flagBusy, handleFlag, onFlagForReview]);

  // Backdrop click handler
  if (!isOpen) return null;

  return (
    <RefetchContext.Provider value={handleChipUpdated}>
      <OverlayPortal>
      <div className="stm-overlay" {...modal.overlayProps}>
        <div
          className="stm-content"
          {...modal.dialogProps}
          aria-label={title}
        >
          <header className="stm-header">
            <div className="stm-title">
              <Database size={18} aria-hidden="true" />
              <h2>{title}</h2>
              {contextLabel && <span className="stm-context">{contextLabel}</span>}
            </div>
            {chips.length > 0 && (
              <div className="stm-chips">
                {chips.map((c, i) => (
                  <span key={i} className={`stm-chip ${c.variant === 'filter' ? 'stm-chip--filter' : ''}`}>
                    {c.icon}
                    <span>{c.label}</span>
                  </span>
                ))}
              </div>
            )}
            <button className="stm-close" onClick={onClose} aria-label="Close">
              <X size={20} />
            </button>
          </header>

          {metaBlock && (metaBlock.description || metaBlock.capability) && (
            <section className="stm-meta-block" aria-label="Context">
              {metaBlock.description && (
                <div className="stm-meta-row">
                  <Info size={14} className="stm-meta-icon" aria-hidden="true" />
                  <div className="stm-meta-text">
                    <span className="stm-meta-label">What it is</span>
                    <p className="stm-meta-body">{metaBlock.description}</p>
                  </div>
                </div>
              )}
              {metaBlock.capability && (
                <div className="stm-meta-row">
                  <Briefcase size={14} className="stm-meta-icon" aria-hidden="true" />
                  <div className="stm-meta-text">
                    <span className="stm-meta-label">Capability</span>
                    <p className="stm-meta-body">{metaBlock.capability}</p>
                  </div>
                </div>
              )}
            </section>
          )}

          <div className="stm-body">
            {isLoading && !allRows.length ? (
              <div className="stm-loading">
                <Loader2 size={24} className="stm-spin" />
                <span>Loading transactions...</span>
              </div>
            ) : error ? (
              <div className="stm-error">
                <AlertCircle size={20} />
                <span>Failed to load: {error.message || String(error)}</span>
              </div>
            ) : (
              <TransactionTable
                vendor={vendor || 'Unknown'}
                departmentId={departmentId}
                component={component}
                productFamily={productFamily}
                startDate={startDate}
                endDate={endDate}
                rows={allRows}
                totalRowCount={allRows.length}
                totalSpend={allRows.reduce((sum, r) => sum + (Number(r.line_total) || 0), 0)}
                isLoading={isLoading}
                error={error}
                defaultConfidenceMode={defaultConfidenceMode}
                hideConfidenceToggle={true}
                groupByPo={true}
                columns={columns}
                renderClassification={renderClassification}
                renderPoLink={renderPoLink}
                renderFooterActions={renderFooterActions}
                copyContext={copyContext}
                showTimePeriodFilter={showTimePeriodFilter}
                className="stm-table"
              />
            )}
          </div>

          {footerNote && (
            <footer className="stm-footer">
              <p>{footerNote}</p>
            </footer>
          )}
        </div>
      </div>
      </OverlayPortal>
    </RefetchContext.Provider>
  );
}

export default SharedTransactionsDrilldownModal;
